import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import MyDialogContent from "@/components/MyDialogContent";
import { useGetData } from "@/hooks/useGetData";
import { formatDate } from "@/utils/helpers";
import type { PatientV2 } from "../types";

type AppointmentRow = {
  id: string;
  appointmentDate: string;
  timeSlot: string;
  provider: string;
  status: string;
  meetingLink: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: PatientV2 | null;
  patientId: string;
};

const PAGE_SIZE = 20;

function text(value: unknown) {
  return String(value ?? "").trim();
}

function recordId(value: unknown) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value && "_id" in value) {
    return text((value as { _id?: string })._id);
  }
  return "";
}

function personName(value: unknown) {
  if (!value || typeof value === "string") return text(value);
  if (typeof value !== "object") return "";
  const row = value as Record<string, unknown>;
  const name = [row.firstName, row.lastName].map(text).filter(Boolean).join(" ");
  return name || text(row.name ?? row.fullName);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function mapAppointment(raw: unknown): AppointmentRow | null {
  const row = asRecord(raw);
  if (!row) return null;
  const id = text(row._id ?? row.id);
  const appointmentDate = text(row.appointmentDate ?? row.date ?? row.scheduledStartAt);
  const timeSlot = text(row.timeSlot);
  if (!id && !appointmentDate && !timeSlot) return null;
  return {
    id: id || `${appointmentDate}-${timeSlot}`,
    appointmentDate,
    timeSlot,
    provider: personName(row.hostProvider ?? row.provider ?? row.hostProviderId),
    status: text(row.status),
    meetingLink: text(
      row.meetingLink ?? row.wherebyUrl ?? row.joinUrl ?? row.roomUrl ?? row.videoLink,
    ),
  };
}

function listFrom(value: unknown): unknown[] | null {
  if (Array.isArray(value)) return value;
  const record = asRecord(value);
  if (!record) return null;
  const nested = [
    record.appointments,
    record.telehealth,
    record.telehealthAppointments,
    record.items,
    record.results,
  ].find(Array.isArray);
  if (Array.isArray(nested)) return nested;
  if (record._id || record.id || record.appointmentDate || record.timeSlot) return [record];
  return null;
}

function extractAppointments(payload: unknown): AppointmentRow[] {
  const root = asRecord(payload);
  const buckets = [payload, root?.data, root?.appointments, root?.telehealth];
  for (const bucket of buckets) {
    const list = listFrom(bucket);
    if (list) return list.map(mapAppointment).filter((row): row is AppointmentRow => Boolean(row));
  }
  return [];
}

function extractTotal(payload: unknown) {
  const root = asRecord(payload);
  const data = asRecord(root?.data) ?? root;
  const raw = data?.total ?? asRecord(data?.pagination)?.total ?? asRecord(data?.pagination)?.count;
  const total = Number(raw);
  return Number.isFinite(total) ? total : undefined;
}

function statusLabel(status: string) {
  if (!status) return "—";
  return status
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function TelehealthAppointmentsDialog({
  open,
  onOpenChange,
  patient,
  patientId,
}: Props) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const organizationId = recordId(patient?.organizationId);

  useEffect(() => {
    if (open) setPage(1);
  }, [open, patientId]);

  const params = useMemo(() => {
    const query: Record<string, string | number> = {
      patientId,
      status: "scheduled",
      page,
      limit: PAGE_SIZE,
    };
    if (organizationId) query.organizationId = organizationId;
    return query;
  }, [organizationId, page, patientId]);

  const { apiData, loading, err } = useGetData(
    "/telehealth",
    params,
    false,
    open && !!patientId,
  );
  const rows = open ? extractAppointments(apiData) : [];
  const total = extractTotal(apiData);
  const totalPages = total != null ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <MyDialogContent heading="Telehealth Appointments" isWide>
        <div className="rounded-lg border bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Time Slot</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Meeting Link</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-sm text-muted-foreground">
                    Loading appointments...
                  </TableCell>
                </TableRow>
              ) : err ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-sm text-red-600">
                    {err}
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-sm text-muted-foreground">
                    No scheduled telehealth appointments.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{formatDate(row.appointmentDate) || "—"}</TableCell>
                    <TableCell>{row.timeSlot || "—"}</TableCell>
                    <TableCell>{row.provider || "—"}</TableCell>
                    <TableCell>
                      <Badge className="border-transparent bg-sky-100 text-sky-800">
                        {statusLabel(row.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {row.meetingLink ? (
                        <button
                          type="button"
                          className="text-sm text-primary underline"
                          onClick={() => {
                            const displayName = [patient?.firstName, patient?.lastName]
                              .filter(Boolean)
                              .join(" ");
                            onOpenChange(false);
                            navigate("/telehealth/room", {
                              state: { room: row.meetingLink, displayName },
                            });
                          }}
                        >
                          Join
                        </button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 ? (
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </MyDialogContent>
    </Dialog>
  );
}
